// @ts-check

import { useCallback, useRef, useState } from "react"

/**
 * @template TItem
 * @param {import("./usePaginatedFetch.hook.types").UsePaginatedFetchParams<TItem>} params
 */
export function usePaginatedFetch(params) {
  const { fetchFn, onError, getItemKey } = params

  const [items, setItems] = useState(/** @type {TItem[]} */ ([]))
  const [totals, setTotals] = useState(/** @type {* | null} */ (null))
  const [hasNextPage, setHasNextPage] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false)

  const abortRef = useRef(/** @type {AbortController | null} */ (null))
  const requestIdRef = useRef(0)
  const isFetchingRef = useRef(false)
  const hasNextPageRef = useRef(true)
  const cursorRef = useRef(/** @type {unknown | null} */ (null))

  const reset = useCallback(() => {
    hasNextPageRef.current = true
    cursorRef.current = null
    setHasNextPage(true)
    setItems([])
    setTotals(null)
  }, [])

  const fetch = useCallback(
    async ({ reset: shouldReset = false } = {}) => {
      if (!shouldReset && (!hasNextPageRef.current || isFetchingRef.current)) {
        return
      }

      if (shouldReset) {
        abortRef.current?.abort()
      }

      isFetchingRef.current = true
      const requestId = ++requestIdRef.current
      const controller = new AbortController()
      abortRef.current = controller

      shouldReset ? setIsLoading(true) : setIsFetchingNextPage(true)

      try {
        const result = await fetchFn({
          cursor: shouldReset ? null : cursorRef.current,
          signal: controller.signal,
        })

        if (requestIdRef.current !== requestId) return

        const { batch, nextCursor, hasMore, totals: newTotals } = result

        if (newTotals !== undefined) {
          setTotals(newTotals)
        }

        hasNextPageRef.current = hasMore
        cursorRef.current = nextCursor
        setHasNextPage(hasMore)

        setItems((prev) => {
          const base = shouldReset ? [] : [...prev]
          const ids = new Set(base.map((item) => getItemKey(item)))

          batch.forEach((item) => {
            if (!ids.has(getItemKey(item))) {
              ids.add(getItemKey(item))
              base.push(item)
            }
          })

          return base
        })
      } catch (error) {
        if (error instanceof Error && error.name !== "AbortError") {
          onError(error)
        }
      } finally {
        if (requestIdRef.current === requestId) {
          isFetchingRef.current = false
          shouldReset ? setIsLoading(false) : setIsFetchingNextPage(false)
        }
      }
    },
    [fetchFn, onError, getItemKey]
  )

  const abort = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return {
    items,
    totals,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetch,
    reset,
    abort,
  }
}
